"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const styled_components_1 = __importDefault(require("styled-components"));
const bridge_1 = require("@completeanatomy/bridge");
const providers_1 = require("../../providers");
const useToolsReducer_1 = require("../../hooks/useToolsReducer");
const ui_1 = require("@completeanatomy/ui");
const bridge_schema_1 = require("@completeanatomy/bridge-schema");
const device_1 = require("@completeanatomy/device");
const drawStyle = {
    lineWidth: 2,
    lineDash: [4, 8],
    circleRadius: 30,
    circleWidth: 2,
};
// eslint-disable-next-line max-lines-per-function
function ToolsCutHole({ width = 0, height = 0, color = '', strokeColor = '', selectedType, }) {
    const [isDrawing, setDrawing] = (0, react_1.useState)(false);
    const [curvePoints, setCurvePoints] = (0, react_1.useState)([]);
    const [fingerPoint, setFingerPoint] = (0, react_1.useState)();
    const bridge = (0, bridge_1.useBridge)();
    const [, dispatch] = (0, providers_1.useToolsModeContext)();
    const canvasRef = (0, react_1.useRef)(null);
    const isRightButton = (0, react_1.useRef)(false);
    const optimalScaleFactor = (0, device_1.useOptimalScreenScale)();
    const deviceScaleFactor = window.devicePixelRatio / (optimalScaleFactor || window.devicePixelRatio);
    const calcPointsForNative = (points) => {
        return points.map(point => {
            return (0, ui_1.multiplyPoint)(point, deviceScaleFactor);
        });
    };
    const formatPoints = (points) => {
        return points.map(point => `{${point.x}, ${point.y}}`);
    };
    const onPaintStartHandler = (0, react_1.useCallback)((event) => {
        isRightButton.current = false;
        if ((0, ui_1.isRightMouseButton)(event)) {
            isRightButton.current = true;
            event.stopPropagation();
            return;
        }
        if (isDrawing || (0, ui_1.isMultiTouchEvent)(event)) {
            return;
        }
        bridge === null || bridge === void 0 ? void 0 : bridge.toolkitOptionsAction({
            type: bridge_schema_1.ToolType.CutHole,
            currentOptions: {
                type: bridge_schema_1.ToolType.CutHole,
                cutMode: selectedType,
                lineIsDrawn: false,
            },
            optionType: 10,
            record: true,
            cutInfo: { action: 'move' },
        });
        bridge === null || bridge === void 0 ? void 0 : bridge.performToolkitAction({
            type: bridge_schema_1.ToolType.CutHole,
            currentOptions: {
                type: bridge_schema_1.ToolType.CutHole,
                cutMode: selectedType,
                lineIsDrawn: false,
            },
        });
        const point = (0, ui_1.getPosition)(event);
        setCurvePoints([...curvePoints, point]);
        setDrawing(true);
    }, [isDrawing, curvePoints, bridge, selectedType]);
    const onPaintHandler = (0, react_1.useCallback)((event) => {
        if (isRightButton.current) {
            event.stopPropagation();
            return;
        }
        if (isDrawing && !(0, ui_1.isMultiTouchEvent)(event)) {
            const point = (0, ui_1.getPosition)(event);
            setCurvePoints([...curvePoints, point]);
            setFingerPoint(point);
            if (curvePoints.length > 2) {
                bridge === null || bridge === void 0 ? void 0 : bridge.cutModelByHole({
                    action: bridge_schema_1.CutModelAction.Move,
                    points: calcPointsForNative(curvePoints),
                });
                bridge === null || bridge === void 0 ? void 0 : bridge.toolkitOptionsAction({
                    type: bridge_schema_1.ToolType.CutHole,
                    currentOptions: {
                        type: bridge_schema_1.ToolType.CutHole,
                        cutMode: selectedType,
                        lineIsDrawn: false,
                    },
                    optionType: 10,
                    record: true,
                    cutInfo: { action: 'move', points: formatPoints(curvePoints) },
                });
            }
        }
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isDrawing, curvePoints, bridge]);
    const onPaintEndHandler = (0, react_1.useCallback)(() => {
        if (isDrawing) {
            setDrawing(false);
            setCurvePoints([]);
            setFingerPoint(undefined);
            if (curvePoints.length > 2) {
                bridge === null || bridge === void 0 ? void 0 : bridge.cutModelByHole({
                    action: bridge_schema_1.CutModelAction.Cut,
                    points: calcPointsForNative(curvePoints),
                });
                bridge === null || bridge === void 0 ? void 0 : bridge.toolkitOptionsAction({
                    type: bridge_schema_1.ToolType.CutHole,
                    currentOptions: {
                        type: bridge_schema_1.ToolType.CutHole,
                        cutMode: selectedType,
                        lineIsDrawn: false,
                    },
                    optionType: 10,
                    record: true,
                    cutInfo: { action: 'cut', points: formatPoints(curvePoints) },
                });
            }
            else {
                bridge === null || bridge === void 0 ? void 0 : bridge.showSnackbarWithType(bridge_schema_1.SnackbarType.DrawCutLine);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDrawing, curvePoints, bridge]);
    (0, react_1.useEffect)(() => {
        if (!canvasRef.current) {
            return;
        }
        const events = (0, ui_1.getPointersEvents)();
        document.addEventListener(events.down, onPaintStartHandler);
        document.addEventListener(events.move, onPaintHandler, true);
        document.addEventListener(events.up, onPaintEndHandler);
        return () => {
            document.removeEventListener(events.down, onPaintStartHandler);
            document.removeEventListener(events.move, onPaintHandler, true);
            document.removeEventListener(events.up, onPaintEndHandler);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onPaintStartHandler, onPaintHandler, onPaintEndHandler]);
    const drawCurve = (0, react_1.useCallback)((context, points) => {
        context.beginPath();
        for (let i = 0; i < points.length - 1; i++) {
            const point1 = points[i];
            const point2 = points[i + 1];
            const midPoint = (0, ui_1.getMidPointBetween)(point1, point2);
            context.quadraticCurveTo(point1.x, point1.y, midPoint.x, midPoint.y);
        }
        context.stroke();
        context.closePath();
    }, []);
    const drawFingerCircle = (0, react_1.useCallback)((context, point) => {
        context.beginPath();
        context.lineWidth = drawStyle.circleWidth;
        context.strokeStyle = color;
        context.setLineDash([]);
        context.arc(point.x, point.y, drawStyle.circleRadius, 0, 2 * Math.PI, false);
        context.stroke();
        context.closePath();
        context.beginPath();
        context.strokeStyle = strokeColor;
        context.arc(point.x, point.y, drawStyle.circleRadius - drawStyle.circleWidth, 0, 2 * Math.PI, false);
        context.stroke();
        context.closePath();
    }, [color, strokeColor]);
    (0, react_1.useEffect)(() => {
        if (!canvasRef.current) {
            return;
        }
        const context = canvasRef.current.getContext('2d');
        if (!context) {
            return;
        }
        context.clearRect(0, 0, width, height);
        context.lineJoin = 'miter';
        context.lineCap = 'square';
        context.strokeStyle = color;
        context.setLineDash([]);
        drawCurve(context, curvePoints);
        context.strokeStyle = strokeColor;
        context.setLineDash(drawStyle.lineDash);
        drawCurve(context, curvePoints);
        if (fingerPoint) {
            drawFingerCircle(context, fingerPoint);
        }
    }, [
        width,
        height,
        color,
        strokeColor,
        isDrawing,
        curvePoints,
        fingerPoint,
        drawCurve,
        drawFingerCircle,
    ]);
    (0, react_1.useEffect)(() => {
        dispatch((0, useToolsReducer_1.toggleCutLineDrawn)(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (0, jsx_runtime_1.jsx)(Canvas, { ref: canvasRef, width: width, height: height, role: "main", "data-e2e": 'ToolsCutHole_Canvas' });
}
exports.default = ToolsCutHole;
const Canvas = styled_components_1.default.canvas `
  position: absolute;
  top: 0;
  left: 0;

  user-select: none;
  pointer-events: none;
`;
